import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { ListItem } from 'material-ui/List';

import Avatar from 'material-ui/Avatar';
import SchoolIcon from 'material-ui/svg-icons/social/school';
import LaunchIcon from 'material-ui/svg-icons/action/launch';
import PrivateIcon from 'material-ui/svg-icons/action/lock';
import SkillIcon from 'material-ui/svg-icons/social/public';
import CPDIcon from 'material-ui/svg-icons/toggle/star';

import TimeLineIcon from 'material-ui/svg-icons/action/timeline';
import SettingsIcon from 'material-ui/svg-icons/action/settings';
import IconMenu from 'material-ui/IconMenu';
import IconButton from 'material-ui/IconButton';
import InfoIcon from 'material-ui/svg-icons/action/info';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import EnrolmentIcon from 'material-ui/svg-icons/action/check-circle';
import LinearProgress from 'material-ui/LinearProgress';

import { avatar as avatarColours, statusColours } from '../../constants/ColourConstants';

import ROLES from '../../constants/Roles';
import buildContextMenu from '../Common/BuildContextMenu';

const style = {
    menu: {
        marginRight: 0,
        marginBottom: 0,
        float: 'left',
        position: 'relative',
        zIndex: 0,
    },
    iconButton: {
        marginRight: 0,
    },
    listItem: {
        padding: '2px',
    }
};

const getActions = (row, auth) => [
    {
        link: `/dashboard/course/${row.id}`,
        label: 'Preview',
        icon: <InfoIcon />
    },
    {
        type:'divider'
    },
    {
        link: `/dashboard/course/${row.id}/enrolments`,
        label: 'Enrolments',
        icon: <EnrolmentIcon />,
        condition: {
            show: auth.canEdit(row.access),
            roles: [ROLES.MANAGER]
        }
    },
    {
        link: `/dashboard/course/${row.id}/activity`,
        label: 'Activity',
        icon: <TimeLineIcon />,
        condition: {
            show: auth.canEdit(row.access),
            roles: [ROLES.MANAGER]
        }
    },
    {
        type:'divider'
    },
    {
        link: `/dashboard/course/${row.id}/settings`,
        label: 'Settings',
        icon: <SettingsIcon />,
        condition: {
            show: auth.canEdit(row.access),
            roles: [ROLES.ADMIN]
        }
    },
];

const CourseListItem = ({data, actions}, {auth}) => {

    const infoLink = `/dashboard/course/${data.id}`;
    const menuItems = buildContextMenu(actions || getActions(data, auth), auth);

    const listItemProps = {
        key: data.id,
        style: style.listItem,
        primaryText: <Link to={infoLink} className="primaryText">{data.name}</Link>
    };

    let displayDuration = '';
    const duration = parseInt(data.duration);

    if (duration > 0) {
        let hours = Math.floor(duration / 60);
        let minutes = duration % 60;

        if (hours > 0) {
            displayDuration = hours + 'h';
            if (minutes > 0) {
                displayDuration += ' ' + minutes + 'm';
            }
        } else if (minutes > 0) {
            displayDuration = minutes + 'm';
        }
        displayDuration = ' - ' + displayDuration;
    }

    let avatarIcon = <SchoolIcon/>;
    let avatarStyle = avatarColours.courseDefault;

    let iconGroup = [];
    if (data.visibility === 'private') {
        iconGroup.push(<PrivateIcon className="icon-small" key="visibility" />);
    }

    if (data.skillId) {
        iconGroup.push(<SkillIcon className="icon-small" key="skill" />);
    }

    if (Number(data.cpdCredits)) {
        iconGroup.push(<CPDIcon className="icon-small" key="cpd" />);
    }

    switch (data.type) {

        case 'classroom':
            avatarIcon = (<SchoolIcon/>);
            break;

        case 'e-learning':
            avatarIcon = (<LaunchIcon/>);
            break;

        default:
            break;
    }

    if (data.loading) {
        listItemProps.secondaryText = <div><LinearProgress mode="indeterminate"/></div>;
    } else if (data.secondaryText) {
        listItemProps.secondaryText = <Link to={infoLink} className="secondaryText">{data.secondaryText}</Link>;
        listItemProps.secondaryTextLines = (data.secondaryTextLines || 2);
    } else {
        const code = (data.code ? data.code : '') + (data.code && data.version ? ' / ' : '') + (data.version ? data.version : '');
        listItemProps.secondaryText = (
            <Link to={infoLink} className="secondaryText">
                <em>{`${data.code}${displayDuration}`}</em>
                <br />
                {iconGroup}
            </Link>
        );
        listItemProps.secondaryTextLines = 2;
    }

    if (menuItems.length > 0) {
        const topRight = {horizontal: 'right', vertical: 'top'};
        const iconMenuProps = {
            menuStyle: style.menu,
            anchorOrigin: topRight,
            targetOrigin: topRight,
            iconButtonElement: (
                <IconButton touch={true}>
                    <MoreVertIcon color={statusColours.light} style={style.iconButton}/>
                </IconButton>
            )
        };
        listItemProps.rightIcon = (
            <IconMenu {...iconMenuProps} >
                {menuItems}
            </IconMenu>
        );

    }

    return (

        <ListItem
            leftAvatar={
                <Link to={infoLink}>
                    <Avatar
                        icon={avatarIcon}
                        style={avatarStyle}
                    />
                </Link>
            }
            {...listItemProps}
        />
    );
};


CourseListItem.contextTypes = {
    auth: PropTypes.object.isRequired
};

export default CourseListItem;
